import {
  faEnvelope,
  faPaperPlaneTop,
  faTrash,
  faWandMagicSparkles
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useEffect, useRef, useState } from 'react'
import z from 'zod'
import { useNewEmailMutation } from '~/api/mutations/action'
import { getApiError } from '~/api/problem'
import { useCompany } from '~/api/queries/company'
import { useOrder } from '~/api/queries/order'
import { useMeInfos } from '~/api/queries/user'
import type { ApiResponseError } from '~/api/utils'
import {
  Checkbox,
  Dropdown,
  EditorInput,
  Form,
  InputCol,
  InputLine,
  Submit,
  TextInput
} from '~/components/Form'
import { SubmitAlignment } from '~/components/Form/SubmitAlignment'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import { EmailTemplateSelector } from '~/components/Scopes/Actions/Components/EmailTemplateSelector'
import { AddressBookPicker } from '~/domains/actions/components/AddressBookPicker'
import { ContactEmailDraftButton } from '~/domains/actions/components/ContactEmailDraftButton'
import { DetailPanel } from '~/domains/actions/components/DetailPanel'
import { DetailToggle } from '~/domains/actions/components/DetailToggle'
import { RecipientList } from '~/domains/actions/components/RecipientList'
import { ActionErrorType, EmailErrorType } from '~/domains/actions/errors'
import { useSendConfirmationFlow } from '~/domains/actions/hooks/useSendConfirmationFlow'
import type {
  AddressBookType,
  RecipientDisplayItem
} from '~/domains/actions/types'
import { useApplicantsHead } from '~/domains/applicants/queries/applicantHead'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import type { StorageObject } from '~/domains/attachments/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { ShortlistUserCommentsField } from '~/domains/orders/components/ShortlistUserCommentsField'
import { ShortlistErrorType } from '~/domains/orders/errors'
import {
  SHORTLIST_PLACEHOLDER,
  toShortlistComments
} from '~/domains/orders/forms/shortlistComments'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { AsideActionContext } from '~/utils/contextes'
import { formatName } from '~/utils/names'
import {
  ccBccToggle,
  ccBccToggleRow,
  emailPrependRow,
  forceSendRow
} from './Actions.css'
import { AsideActionsItems } from './AsideActionsItems'
import { EmailApplicantAttachmentsSelector } from './Components/EmailApplicantAttachmentsSelector'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z.object({
  subject: z
    .string({ message: 'Le sujet ne peut pas être vide' })
    .min(1, 'Le sujet ne peut pas être vide'),
  message: z.string({ message: 'Le message ne peut pas être vide' }),
  sendFrom: z.string(),
  attachments: z
    .array(
      z.object({
        fromTemplate: z.boolean(),
        object: z.object({
          filename: z.string().optional(),
          base64_content: z.string().optional()
        })
      })
    )
    .optional(),
  templateId: z.preprocess(
    (val) => (typeof val === 'number' && !Number.isNaN(val) ? val : undefined),
    z.number().optional()
  ),
  cc: z.array(z.string()).optional(),
  bcc: z.array(z.string()).optional(),
  shortlistComments: z.record(z.string(), z.string()).optional()
})

type FormValues = z.infer<typeof formSchema>

type EmailActionProps = {
  applicantIds?: number[]
  contactsIds?: number[]
  removeApplicantIdFromList?: (applicantId: number) => void
  onClose?: () => void
  orderContext?: {
    id: number
    willChangeStatus: boolean
  }
  sendTo?: 'applicant' | 'contact'
  formId?: string
}

export const EmailAction = ({
  applicantIds,
  contactsIds,
  removeApplicantIdFromList,
  onClose,
  orderContext,
  sendTo,
  formId
}: EmailActionProps) => {
  const applicantDataHeadQueries = useApplicantsHead<
    ApplicantHeadData & {
      attachments?: {
        id: number
        object: StorageObject
      }[]
    }
  >({
    applicantIds: applicantIds ?? [],
    additionalFields: sendTo === 'contact' ? ['attachments'] : undefined
  })
  const applicantDataHead = applicantDataHeadQueries
    .map((query) => query.data)
    .filter((data) => data !== undefined)
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses', {
    enabled: !!orderContext?.id
  })
  const [error, setError] = useState<string | null>(null)
  const [currentApplicantIds, setCurrentApplicantIds] = useState<number[]>(
    applicantIds ?? []
  )
  const [optionsValues, setOptionsValues] = useState<{
    notificationReceived: boolean
    notificationRead: boolean
  }>({
    notificationReceived: false,
    notificationRead: false
  })
  const closeAction = useCloseAction()
  const { setIsDirty } = useContext(AsideActionContext)
  const [attachmentsIds, setAttachmentsIds] = useState<number[]>([])
  const [uploading, setUploading] = useState(false)
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const userInfo = useMeInfos()
  const newEmailMutation = useNewEmailMutation()

  const { data: orderData, isLoading: isOrderLoading } = useOrder(
    orderContext?.id
  )
  const companyId = orderData?.company?.id
  // The main recipients type is managed by the dedicated recipients field:
  // don't offer the same type again in cc/bcc.
  const ccBccTypes: AddressBookType[] =
    sendTo === 'contact'
      ? ['user', 'applicant', 'external']
      : ['user', 'contact', 'external']
  const { data: companyData } = useCompany(companyId)
  const defaultContact = orderData?.contact.id.toString()

  let formSchemaWithContext = formSchema
  if (sendTo === 'contact') {
    formSchemaWithContext = formSchema.extend({
      contacts: z
        .array(z.string())
        .min(1, 'Au moins un destinataire est requis')
    })
  }

  const onActionClose = (e?: React.MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault()
    }
    closeAction().then((confirm) => {
      if (confirm) {
        onClose?.()
      }
    })
  }

  type EmailMutationPayload = Parameters<
    ReturnType<typeof useNewEmailMutation>['mutateAsync']
  >[0]
  const lastPayloadRef = useRef<EmailMutationPayload | null>(null)

  const {
    invalidRecipients,
    showInvalidDetail,
    setShowInvalidDetail,
    allRecipientsInvalid: allRecipientsInvalidEmail,
    setAllRecipientsInvalid: setAllRecipientsInvalidEmail,
    skippedAfterSendRecipients,
    showSkippedDetail,
    setShowSkippedDetail,
    resetBeforeSend,
    handleSendSuccess
  } = useSendConfirmationFlow({
    sentToastTitle: 'Email envoyé',
    sentToastMessage: "L'email a bien été envoyé",
    onFullySent: () => {
      closeAction(true)
      onClose?.()
    }
  })

  // Handle applicantIds change from parent if new action is triggered
  useEffect(() => {
    if (applicantIds) {
      setCurrentApplicantIds(applicantIds)
      resetBeforeSend()
    }
  }, [applicantIds, resetBeforeSend])

  const handleSendError = (error: ApiResponseError) => {
    const problem = getApiError(error)
    if (problem?.type === EmailErrorType.AllRecipientsInvalidEmail) {
      const invalidApplicantIds = Array.isArray(
        problem.extensions.invalidApplicants
      )
        ? (problem.extensions.invalidApplicants as number[])
        : []
      const invalidContactIds = Array.isArray(
        problem.extensions.invalidContacts
      )
        ? (problem.extensions.invalidContacts as number[])
        : []
      setAllRecipientsInvalidEmail({
        applicantIds: invalidApplicantIds,
        contactIds: invalidContactIds
      })
      return
    }
    if (problem?.type === ActionErrorType.RecipientInvalidEmail) {
      const email = problem.extensions.email
      setError(
        typeof email === 'string' && email !== ''
          ? `L'adresse « ${email} » d'un destinataire en copie est invalide`
          : "Un destinataire en copie n'a pas d'adresse email"
      )
      return
    }
    if (problem?.type === ActionErrorType.RecipientNotAllowed) {
      setError(
        "Vous n'êtes pas autorisé à ajouter l'un des destinataires en copie"
      )
      return
    }
    if (problem?.type === ActionErrorType.AttachmentNotAllowed) {
      setError(
        "Vous n'êtes pas autorisé à joindre un ou plusieurs de ces documents"
      )
      return
    }
    if (problem?.type === ShortlistErrorType.ApplicantRecipient) {
      setError("La présentation client ne peut être envoyée qu'à des contacts")
      return
    }
    if (problem?.type === ShortlistErrorType.MissingOrder) {
      setError('La présentation client doit être envoyée depuis une commande')
      return
    }
    if (problem?.type === ShortlistErrorType.CopiedRecipients) {
      setError(
        'La présentation client ne peut pas être envoyée avec des destinataires en copie'
      )
      return
    }
    if (problem?.type === ShortlistErrorType.MissingApplicants) {
      setError('Sélectionnez au moins un candidat pour la présentation client')
      return
    }
    if (problem?.type === ActionErrorType.RecipientOutsideOrderCompany) {
      setError(
        "Un contact en copie n'appartient pas à l'entreprise de la commande"
      )
      return
    }
    const potentialContactMissEmail = error.response?.message?.match(
      /Contact (\d+) does not have an email address/i
    )?.[1]
    if (potentialContactMissEmail) {
      setError('Veuillez vérifier que les contacts ont bien une adresse email')
      return
    }
    setError('Une erreur est survenue')
  }

  const onSubmit = async (
    values: FormValues & { contacts?: string[]; cc?: string[]; bcc?: string[] }
  ) => {
    setError(null)
    resetBeforeSend()

    // Parse sendFrom to extract userId or agencyId
    let userId: number | undefined
    let agencyId: number | undefined
    if (values.sendFrom && values.sendFrom !== '') {
      const match = values.sendFrom.match(/^(user|agency):(\d+)$/)
      if (match?.[1] && match?.[2]) {
        const type = match[1]
        const id = Number.parseInt(match[2], 10)
        if (type === 'user') {
          userId = id
        } else if (type === 'agency') {
          agencyId = id
        }
      }
    }

    const payload: EmailMutationPayload = {
      applicants: currentApplicantIds,
      contacts: sendTo === 'contact' ? values.contacts?.map(Number) : undefined,
      cc: values.cc,
      bcc: values.bcc,
      emailTo: sendTo ?? 'applicant',
      userId,
      agencyId,
      templateId: values.templateId,
      subject: values.subject,
      content: values.message,
      notificationRead: optionsValues.notificationRead,
      notificationReceived: optionsValues.notificationReceived,
      attachmentsIds,
      shortlistComments: values.message.includes(SHORTLIST_PLACEHOLDER)
        ? toShortlistComments(currentApplicantIds, values.shortlistComments)
        : undefined,
      attachments: values.attachments
        ?.filter(
          (attachment) =>
            !attachment.fromTemplate &&
            attachment.object?.base64_content &&
            attachment.object?.filename
        )
        .map(
          (attachment) =>
            attachment.object as {
              base64_content: string
              filename: string
            }
        ),
      order: orderData
        ? {
            qualification: orderData.qualification ?? { id: 0, name: '' }, //(TEMP, all orders should have a qualification)
            ...orderData
          }
        : undefined
    }
    lastPayloadRef.current = payload

    await newEmailMutation.mutateAsync(payload, {
      onSuccess: handleSendSuccess,
      onError: handleSendError
    })
  }

  const onForce = async () => {
    if (!lastPayloadRef.current) {
      return
    }
    resetBeforeSend()
    await newEmailMutation.mutateAsync(
      { ...lastPayloadRef.current, force: true },
      { onSuccess: handleSendSuccess, onError: handleSendError }
    )
  }

  const contactCount = contactsIds?.length ?? 1
  const actionTitle = `Email${
    sendTo === 'applicant'
      ? ` au${currentApplicantIds.length > 1 ? 'x' : ''} candidat${currentApplicantIds.length > 1 ? 's' : ''}`
      : ` au${contactCount > 1 ? 'x' : ''} contact${contactCount > 1 ? 's' : ''}`
  }`

  if (
    !userInfo ||
    isOrderLoading ||
    (sendTo === 'applicant' &&
      applicantDataHeadQueries.some(
        (query) => query.isLoading || query.isError
      ))
  ) {
    return (
      <AsideAction
        title={actionTitle}
        icon={faEnvelope}
        applicantIds={
          sendTo === 'applicant' ? (currentApplicantIds ?? []) : undefined
        }
        contactIds={sendTo === 'contact' ? contactsIds : undefined}
        onClose={onClose}
      >
        {applicantDataHeadQueries.some((query) => query.isLoading) ? (
          <Loader small />
        ) : (
          <Alert
            message={`Une erreur est survenue lors de la récupération ${applicantDataHeadQueries.length > 1 ? 'des' : 'du'} ${applicantDataHeadQueries.length > 1 ? 'candidats' : 'candidat'}.`}
            variation={AlertVariation.Danger}
            onDismiss={onActionClose}
          />
        )}
      </AsideAction>
    )
  }

  if (skippedAfterSendRecipients) {
    const skippedLabel = sendTo === 'contact' ? 'contact' : 'candidat'
    const skippedRecipients: RecipientDisplayItem[] = [
      ...skippedAfterSendRecipients.applicantIds.map(
        (id): RecipientDisplayItem => {
          const data = applicantDataHead?.find(
            (applicant) => applicant.id === id
          )
          return {
            id,
            type: 'applicant',
            label: data ? formatName(data) : `${skippedLabel} #${id}`,
            href: data ? `${legacyUrl}/admin/candidats/edit/${id}` : undefined
          }
        }
      ),
      ...skippedAfterSendRecipients.contactIds.map(
        (id): RecipientDisplayItem => ({
          id,
          type: 'contact',
          label: `${skippedLabel} #${id}`
        })
      )
    ]

    return (
      <AsideAction
        title={actionTitle}
        icon={faEnvelope}
        applicantIds={
          sendTo === 'applicant' ? (currentApplicantIds ?? []) : undefined
        }
        contactIds={sendTo === 'contact' ? contactsIds : undefined}
        onClose={onClose}
      >
        <Alert
          message="L'email a bien été envoyé"
          variation={AlertVariation.Success}
          onDismiss={() => {
            closeAction(true)
            onClose?.()
          }}
        />
        <Alert
          message="Les destinataires sans adresse email valide n'ont pas reçu l'email"
          variation={AlertVariation.Warning}
        >
          <DetailToggle
            isOpen={showSkippedDetail}
            onToggle={() => setShowSkippedDetail((v) => !v)}
          />
          <DetailPanel isOpen={showSkippedDetail}>
            <RecipientList recipients={skippedRecipients} />
          </DetailPanel>
        </Alert>
      </AsideAction>
    )
  }

  if (
    userInfo &&
    !isOrderLoading &&
    (sendTo !== 'contact' ||
      !applicantDataHeadQueries.some((query) => query.isLoading))
  ) {
    const fromEmail = [
      {
        label: formatName(userInfo),
        value: `user:${userInfo.id}`
      }
    ]
    for (const agency of userInfo.agencies) {
      fromEmail.push({
        label: agency.name,
        value: `agency:${agency.id}`
      })
    }

    const canGenerateAiDraft =
      sendTo === 'contact' &&
      currentApplicantIds.length === 1 &&
      (orderContext?.id ?? 0) > 0

    let prepend = <></>
    if (
      sendTo === 'contact' &&
      applicantDataHead &&
      applicantDataHead.length > 0
    ) {
      prepend = (
        <div className={emailPrependRow}>
          <EmailApplicantAttachmentsSelector
            applicantDataHead={applicantDataHead}
            setAttachmentsIds={setAttachmentsIds}
            attachmentsIds={attachmentsIds}
          />
          {canGenerateAiDraft && currentApplicantIds[0] && orderContext?.id && (
            <ContactEmailDraftButton
              applicantId={currentApplicantIds[0]}
              orderId={orderContext.id}
              disabled={uploading}
            />
          )}
        </div>
      )
    }

    const nextStatus = orderStatuses?.find(
      (status) => status.specialStatus === 'addressed'
    )?.name

    const recipientLabel = sendTo === 'contact' ? 'contact' : 'candidat'
    const toDisplayItems = (
      ids: { applicantIds: number[]; contactIds: number[] } | null
    ): RecipientDisplayItem[] =>
      ids
        ? [
            ...ids.applicantIds.map((id): RecipientDisplayItem => {
              const data = applicantDataHead?.find(
                (applicant) => applicant.id === id
              )
              return {
                id,
                type: 'applicant',
                label: data ? formatName(data) : `${recipientLabel} #${id}`,
                href: data
                  ? `${legacyUrl}/admin/candidats/edit/${id}`
                  : undefined,
                removable: Boolean(data)
              }
            }),
            ...ids.contactIds.map(
              (id): RecipientDisplayItem => ({
                id,
                type: 'contact',
                label: `${recipientLabel} #${id}`
              })
            )
          ]
        : []

    const invalidRecipientsForDisplay = toDisplayItems(invalidRecipients)
    const allRecipientsInvalidEmailForDisplay = toDisplayItems(
      allRecipientsInvalidEmail
    )

    return (
      <Form
        id={formId}
        onSubmit={onSubmit}
        schema={formSchemaWithContext}
        defaultValues={{
          sendFrom: `user:${userInfo.id}`,
          contacts:
            sendTo === 'contact' && !orderContext?.id
              ? contactsIds?.map(String)
              : undefined
        }}
        globalError={error}
        globalErrorInSubmit={true}
        onDirtyStateChange={(isDirty) => {
          if (isDirty) {
            setIsDirty(true)
          }
        }}
      >
        <AsideAction
          title={actionTitle}
          icon={faEnvelope}
          applicantIds={
            sendTo === 'applicant' ? (currentApplicantIds ?? []) : undefined
          }
          contactIds={sendTo === 'contact' ? contactsIds : undefined}
          onClose={onClose}
        >
          <AsideActionsItems>
            <Button
              variation={ButtonVariation.DangerInversed}
              type="button"
              onClick={onActionClose}
              buttonSize="small"
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
            <Submit
              buttonVariation={ButtonVariation.Primary}
              loadingText="Envoi..."
              successText={
                invalidRecipients || allRecipientsInvalidEmail
                  ? 'Envoyer'
                  : 'Envoi en cours'
              }
              errorText="Erreur de saisie"
              onSuccessReset={false}
              submitAlignment={SubmitAlignment.Right}
              buttonSize="small"
              disabled={uploading}
            >
              <FontAwesomeIcon icon={faPaperPlaneTop} />
              Envoyer
            </Submit>
          </AsideActionsItems>
          {(allRecipientsInvalidEmail || invalidRecipients) && (
            <>
              {allRecipientsInvalidEmail && (
                <Alert
                  variation={AlertVariation.Danger}
                  message={`${allRecipientsInvalidEmailForDisplay.length} ${recipientLabel}${allRecipientsInvalidEmailForDisplay.length > 1 ? 's' : ''} n'${allRecipientsInvalidEmailForDisplay.length > 1 ? 'ont' : 'a'} pas d'adresse email valide, l'envoi n'a pas pu être effectué`}
                  onDismiss={onActionClose}
                >
                  <RecipientList
                    recipients={allRecipientsInvalidEmailForDisplay}
                    canRemove={(currentApplicantIds?.length ?? 0) > 1}
                    onRemove={
                      removeApplicantIdFromList
                        ? (id, type) => {
                            if (type !== 'applicant') return
                            removeApplicantIdFromList(id)
                            setCurrentApplicantIds((prev) =>
                              prev.filter((appId) => appId !== id)
                            )
                            setAllRecipientsInvalidEmail((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    applicantIds: prev.applicantIds.filter(
                                      (appId) => appId !== id
                                    )
                                  }
                                : prev
                            )
                          }
                        : undefined
                    }
                  />
                </Alert>
              )}
              {invalidRecipients && (
                <Alert
                  variation={AlertVariation.Warning}
                  message={`${invalidRecipientsForDisplay.length} ${recipientLabel}${invalidRecipientsForDisplay.length > 1 ? 's' : ''} n'${invalidRecipientsForDisplay.length > 1 ? 'ont' : 'a'} pas d'adresse email valide`}
                >
                  <DetailToggle
                    isOpen={showInvalidDetail}
                    onToggle={() => setShowInvalidDetail((v) => !v)}
                  />
                  <DetailPanel isOpen={showInvalidDetail}>
                    <RecipientList recipients={invalidRecipientsForDisplay} />
                  </DetailPanel>
                  <div className={forceSendRow}>
                    <InputLine justify="end">
                      <Button
                        variation={ButtonVariation.Primary}
                        type="button"
                        onClick={onForce}
                        buttonSize="small"
                        disabled={newEmailMutation.isPending}
                      >
                        <FontAwesomeIcon icon={faPaperPlaneTop} />
                        {newEmailMutation.isPending
                          ? 'Envoi...'
                          : "Poursuivre l'envoi"}
                      </Button>
                    </InputLine>
                  </div>
                </Alert>
              )}
            </>
          )}
          {currentApplicantIds.length > 0 &&
            sendTo === 'contact' &&
            orderContext?.id &&
            orderContext?.willChangeStatus &&
            nextStatus && (
              <Alert
                variation={AlertVariation.Info}
                message={`L'envoi d'email changera automatiquement le statut des candidats vers « ${nextStatus} ».`}
                icon={faWandMagicSparkles}
              />
            )}
          <InputCol>
            <InputLine>
              <Dropdown
                name="sendFrom"
                options={fromEmail}
                label="De"
                onChange={() => setIsDirty(true)}
                required
              />
              <EmailTemplateSelector
                name="templateId"
                subjectFieldName="subject"
                contentFieldName="message"
                onChange={() => setIsDirty(true)}
              />
            </InputLine>

            {companyData && orderData && sendTo === 'contact' && (
              <Dropdown
                name="contacts"
                label="Pour"
                defaultValues={defaultContact ? [defaultContact] : []}
                options={
                  companyData?.contacts
                    ?.filter((contact) => !contact.archived)
                    ?.map((contact) => ({
                      label: formatName(contact),
                      value: contact.id.toString()
                    })) || []
                }
                placeholder="Sélectionner un ou plusieurs contacts"
                multiple={true}
                onChange={() => setIsDirty(true)}
              />
            )}
            {showCc && (
              <AddressBookPicker
                name="cc"
                label="Cc"
                placeholder="Ajouter des destinataires en copie"
                allowFreeText
                orderCompanyId={companyId}
                types={ccBccTypes}
                onChange={() => setIsDirty(true)}
              />
            )}
            {(!showCc || !showBcc) && (
              <div className={ccBccToggleRow}>
                {!showCc && (
                  <button
                    type="button"
                    className={ccBccToggle}
                    onClick={() => setShowCc(true)}
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    type="button"
                    className={ccBccToggle}
                    onClick={() => setShowBcc(true)}
                  >
                    Cci
                  </button>
                )}
              </div>
            )}
            {showBcc && (
              <AddressBookPicker
                name="bcc"
                label="Cci"
                placeholder="Ajouter des destinataires en copie cachée"
                allowFreeText
                orderCompanyId={companyId}
                types={ccBccTypes}
                onChange={() => setIsDirty(true)}
              />
            )}
            <TextInput name="subject" placeholder="Sujet" />
            <EditorInput
              name="message"
              prepend={prepend}
              append={
                sendTo === 'contact' &&
                orderContext?.id &&
                applicantDataHead &&
                applicantDataHead.length > 0 ? (
                  <ShortlistUserCommentsField applicants={applicantDataHead} />
                ) : undefined
              }
              compactToolbar
              rows={5}
              required
              autofocus={false}
              hasAttachments
              placeholderCategories={
                orderContext?.id
                  ? [
                      'Candidat',
                      'Contact',
                      'Entreprise',
                      'Agence',
                      'Autres',
                      'Commande',
                      'Offre'
                    ]
                  : sendTo === 'contact'
                    ? ['Contact', 'Entreprise', 'Agence', 'Autres']
                    : ['Candidat', 'Agence', 'Autres']
              }
              onChange={() => setIsDirty(true)}
              options={
                <InputCol>
                  <Checkbox
                    defaultChecked={false}
                    label="Demander un accusé de réception"
                    onChange={(checked) => {
                      setOptionsValues((prev) => ({
                        ...prev,
                        notificationReceived: checked
                      }))
                      setIsDirty(true)
                    }}
                  />
                  <Checkbox
                    defaultChecked={false}
                    label="Demander une confirmation de lecture"
                    onChange={(checked) => {
                      setOptionsValues((prev) => ({
                        ...prev,
                        notificationRead: checked
                      }))
                      setIsDirty(true)
                    }}
                  />
                </InputCol>
              }
              onUploading={(uploading) => setUploading(uploading)}
            />
          </InputCol>
        </AsideAction>
      </Form>
    )
  }
}
